import { useTranslation } from "react-i18next";

//components
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { SplitButton } from "primereact/splitbutton";
import Avatar from "react-avatar";
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { DataTableCard } from "./styled";

//utils
import { FaEllipsisH, FaExternalLinkAlt, FaEye, FaLink } from "react-icons/fa";
import { MdOutlineDriveFolderUpload } from "react-icons/md";
import { Link } from "react-router-dom";
import { formatDate } from "../../../../../helpers";
import { colors } from "../../../../../theme/theme";
import handleLongText from "../../../../utils/handleLongText/handleLongText";
import { ActionItemLink } from "../action-item-link";

export function UsersTable(props: any) {
	const { t: translation } = useTranslation();
	const {
		nodes,
		treeFields,
		handleSignature,
		handlePartnerActive,
		handleCancelSignature,
		handleTableSort,
		sortField,
		sortOrder,
		handleUserDetail,
		handleRemoveUser,
	} = props;

	function nameTemplate(node: any) {
		let name = node.name ?? "Não informado";
		let responsible = node.username ?? "Nenhum responsável informado";
		return (
			<Row>
				<Col
					sm={2}
					md={2}
					lg={1}
					className="d-flex align-items-center photo-name-template hide-photo"
					style={{ paddingRight: "0px" }}
				>
					<Link to={handleUserDetail(node)}>
						<Avatar
							size="28"
							style={{ cursor: "pointer" }}
							round={true}
							name={node.name !== "" ? node.name : "U S"}
							src={node.picture ?? ""}
							color={
								!node.picture ? colors.mainColors.blueberry : ""
							}
						/>
					</Link>
				</Col>
				<Col
					sm={10}
					md={10}
					lg={11}
				>
					<span
						onClick={() => handleUserDetail(node)}
						style={{ cursor: "pointer" }}
					>
						<b>{handleLongText(name, 27)}</b>
					</span>
					<br />
					<span className="text-dark-grey">
						{translation("manager.user.responsible")}:{" "}
						{handleLongText(responsible, 27)}
					</span>
				</Col>
			</Row>
		);
	}

	function infoUseTemplate(node: any) {
		if (node.status === "Visitante") {
			return (
				<div className="text-start ml-4 font-14">
					<FaEye className="mb-2" />{" "}
					<span>
						{translation("manager.user.viewedMaterials")}:{" "}
						{node.user_information
							? node.user_information.visualized_materials
							: 0}
					</span>
				</div>
			);
		} else if (node.status === "Parceiro") {
			return (
				<div className="text-start ml-4 font-14">
					<MdOutlineDriveFolderUpload className="mb-2" />{" "}
					<span>
						{translation("manager.user.registeredMaterials")}:{" "}
						{node.user_information
							? node.user_information.registered_materials
							: 0}
					</span>
				</div>
			);
		} else {
			return (
				<div className="text-start ml-4 font-14">
					<FaLink className="mt-2 mb-2" />{" "}
					<span>
						{translation("manager.user.usedMaterials")}:{" "}
						{node.user_information
							? node.user_information.materials_used
							: 0}
					</span>
					<br />
					<FaExternalLinkAlt className="mb-2" />{" "}
					<span>
						{translation("manager.user.externalMaterials")}:{" "}
						{node.user_information
							? node.user_information?.external_access
							: 0}
					</span>
				</div>
			);
		}
	}

	function infoSignatureTemplate(node: any) {
		let signature = node.signature ? (
			<>
				<span className="font-14">
					{translation("manager.user.hiring")}:{" "}
					{formatDate(node.signature.start)}
				</span>
				<br />
				<span className="font-14">
					{translation("manager.user.dueDate")}:{" "}
					{formatDate(node.signature.end)}
				</span>
			</>
		) : (
			<span className="font-14">Não possui assinatura</span>
		);

		return signature;
	}

	function lastLoginTemplate(node: any) {
		let lastLogin = node.last_login
			? `Há ${node.last_login} ${node.last_login > 1 ? "dias" : "dia"}`
			: "Nunca fez login";
		return <span className="font-14">{lastLogin}</span>;
	}

	function actionsTemplate(node: any) {
		let items: any = [];

		items.push({
			template: (
				<ActionItemLink url={handleUserDetail(node)}>
					Visualizar perfil
				</ActionItemLink>
			),
		});

		switch (node.status) {
			case "Parceiro":
				items.push({
					template: (
						<ActionItemLink url={handleUserDetail(node, true)}>
							{translation("userAccount.registeredMaterials")}
						</ActionItemLink>
					),
				});

				if (node.is_active) {
					items.push({
						label: "Inativar parceiro",
						command: () => {
							handlePartnerActive(node);
						},
					});
				} else {
					items.push({
						label: "Reativar parceiro",
						command: () => {
							handlePartnerActive(node);
						},
					});
					items.push({
						label: "Excluir Usuário",
						command: () => {
							handleRemoveUser(node);
						},
					});
				}
				break;
			case null:
			case "Visitante":
				items.push({
					label: "Excluir Usuário",
					command: () => {
						handleRemoveUser(node);
					},
				});

				items.push({
					template: (
						<ActionItemLink url={handleUserDetail(node, true)}>
							{translation("userAccount.viewedMaterials")}
						</ActionItemLink>
					),
				});

				if (node.signature && node.signature.is_active) {
					items.push({
						label: "Reativar Assinatura",
						command: () => {
							handleSignature(node, true);
						},
					});
				} else {
					items.push({
						label: "Criar Assinatura",
						command: () => {
							handleSignature(node, true);
						},
					});
				}
				break;

			case "Assinante":
				items.push({
					label: "Excluir Usuário",
					command: () => {
						handleRemoveUser(node);
					},
				});

				items.push({
					template: (
						<ActionItemLink url={handleUserDetail(node, true)}>
							{translation("userAccount.usedMaterials")}
						</ActionItemLink>
					),
				});

				if (node.signature) {
					items.push({
						label: "Renovar Assinatura",
						command: () => {
							handleSignature(node, false);
						},
					});

					items.push({
						label: "Cancelar Assinatura",
						command: (e: any) => {
							handleCancelSignature(node);
						},
					});
				} else {
					items.push({
						label: "Criar Assinatura",
						command: () => {
							handleSignature(node, true);
						},
					});
				}
				break;
		}

		return (
			<OverlayTrigger
				placement="top"
				overlay={<Tooltip>Opções</Tooltip>}
			>
				<div>
					<SplitButton
						label=""
						className="p-button p-component p-button-rounded p-button-text p-button-icon-only"
						buttonClassName="d-none"
						dropdownIcon={<FaEllipsisH />}
						model={items}
					/>
				</div>
			</OverlayTrigger>
		);
	}

	function dateCaptureTemplate(node: any) {
		return <span>{formatDate(node.created_at)}</span>;
	}

	function rowClassName(node: any) {
		if (node.id) {
			return {
				"p-inactive": node.is_active === false,
				"p-subscriber": node.status === "Assinante",
				"p-partner": node.status === "Parceiro",
				"p-guest": node.status === "Visitante" || node.status === null,
			};
		} else {
			return {
				"p-lead": true,
			};
		}
	}

	return (
		<div>
			<DataTableCard>
				<DataTable
					value={nodes}
					rowClassName={rowClassName}
					sortField={sortField}
					sortOrder={sortOrder}
					onSort={(e) => handleTableSort(e)}
					responsiveLayout="scroll"
				>
					{!!treeFields?.length &&
						treeFields?.map((col: any) => {
							if (col.nameTemplate) {
								return (
									<Column
										key={col.field}
										body={nameTemplate}
										field={col.field}
										header={col.header}
										style={col.style}
										{...col}
									/>
								);
							} else if (col.actionsTemplate) {
								return (
									<Column
										key={col.field}
										body={actionsTemplate}
										field={col.field}
										header={col.header}
										style={col.style}
										{...col}
									/>
								);
							} else if (col.infoSignatureTemplate) {
								return (
									<Column
										key={col.field}
										body={infoSignatureTemplate}
										field={col.field}
										header={col.header}
										style={col.style}
										{...col}
									/>
								);
							} else if (col.infoUseTemplate) {
								return (
									<Column
										key={col.field}
										body={infoUseTemplate}
										field={col.field}
										header={col.header}
										style={col.style}
										{...col}
									/>
								);
							} else if (col.lastLoginTemplate) {
								return (
									<Column
										key={col.field}
										body={lastLoginTemplate}
										field={col.field}
										header={col.header}
										style={col.style}
										{...col}
									/>
								);
							} else if (col.dateCaptureTemplate) {
								return (
									<Column
										key={col.field}
										body={dateCaptureTemplate}
										field={col.field}
										header={col.header}
										style={col.style}
										{...col}
									/>
								);
							} else {
								return (
									<Column
										key={col.field}
										field={col.field}
										header={col.header}
										style={col.style}
										{...col}
									/>
								);
							}
						})}
					{props.children}
				</DataTable>
			</DataTableCard>
		</div>
	);
}
